export class CookieHelper {
  public getItem = (key: string): string =>
    document.cookie.split("; ").reduce((total, currentCookie) => {
      const item = currentCookie.split("=");
      const storedKey = item[0];
      const storedValue = item[1];

      return key === storedKey ? decodeURIComponent(storedValue) : total;
    }, "");

  /**
   * Every cookie this app writes goes through here, which is what makes it
   * the one place the security flags can be applied (PPI-1848, closing
   * architecture risk #7).
   *
   * `SameSite=Lax` is unconditional: it stops the cookie being sent on
   * cross-site subrequests while still allowing it on a top-level GET
   * navigation — which is exactly the inbound `?token=` email-link flow
   * `useRequireAuth` handles. `Strict` would buy nothing this app needs and
   * risks that flow.
   *
   * `Secure` is conditional on the page's own protocol, deliberately NOT on
   * `isLocalEnvironment`. A `Secure` cookie is silently dropped by the
   * browser over plain HTTP and local dev serves http://localhost:3000, so
   * an unconditional flag would mean the token cookie is never stored and
   * nobody could sign in locally. Gating on the environment flag would be
   * wrong the moment a local setup is proxied behind HTTPS, or a deployed
   * environment is served over HTTP — the protocol is the actual determinant
   * of whether `Secure` is safe to set.
   *
   * Not set: `HttpOnly`. The token is read by JavaScript by design — the
   * axios interceptor in HttpClient attaches it to every request — so it
   * cannot be HttpOnly without redesigning how requests are authenticated.
   * That is why PPI-1097's XSS findings are load-bearing for session
   * security here, as PPI-1873 notes.
   */
  public setItem = (
    key: string,
    value: string | null,
    numberOfDays: number
  ): void => {
    const now = new Date();

    // set the time to be now + numberOfDays
    now.setTime(now.getTime() + numberOfDays * 60 * 60 * 24 * 1000);

    const isSecureContext = window.location.protocol === "https:";

    document.cookie = `${key}=${value}; expires=${now.toUTCString()}; path=/; domain=${
      window.location.hostname
    }; SameSite=Lax${isSecureContext ? "; Secure" : ""}`;
  };
}
